import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Mail, Lock, User, Phone, Building2, MapPin, Home, 
  Euro, Users, Clock, Camera, CheckCircle2, 
  Loader2, Eye, EyeOff, AlertCircle, ArrowRight, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const PropertyRegistrationForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Step 1: Account Info
    email: '',
    password: '',
    confirmPassword: '',
    nome: '',
    cognome: '',
    telefono: '',
    
    // Step 2: Property Info
    property_name: '',
    property_type: 'hotel',
    city: '',
    address: '',
    description: '',
    
    // Step 3: Amenities & Policies
    amenities: [],
    checkin_from: '14:00',
    checkin_to: '22:00',
    checkout_from: '08:00',
    checkout_to: '11:00',
    
    // Step 4: Room/Unit (basic info)
    room_name: 'Camera Standard',
    room_capacity: 2,
    price_per_night: 80,
    
    // Legal checkboxes
    acceptTerms: false,
    acceptPrivacy: false
  });

  const propertyTypes = [
    { value: 'hotel', label: 'Hotel' },
    { value: 'bnb', label: 'B&B' },
    { value: 'appartamento', label: 'Appartamento' },
    { value: 'villa', label: 'Villa' },
    { value: 'ostello', label: 'Ostello' },
    { value: 'casa_vacanze', label: 'Casa Vacanze' }
  ];

  const availableAmenities = [
    { id: 'wifi', label: 'Wi-Fi Gratuito', icon: '📶' },
    { id: 'parking', label: 'Parcheggio', icon: '🅿️' },
    { id: 'breakfast', label: 'Colazione Inclusa', icon: '🍳' },
    { id: 'ac', label: 'Aria Condizionata', icon: '❄️' },
    { id: 'pool', label: 'Piscina', icon: '🏊' },
    { id: 'gym', label: 'Palestra', icon: '💪' },
    { id: 'spa', label: 'Spa', icon: '💆' },
    { id: 'restaurant', label: 'Ristorante', icon: '🍽️' },
    { id: 'pet_friendly', label: 'Animali Ammessi', icon: '🐕' },
    { id: 'wheelchair', label: 'Accessibile', icon: '♿' }
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.email) {
        newErrors.email = 'Email obbligatoria';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email non valida';
      }

      if (!formData.password) {
        newErrors.password = 'Password obbligatoria';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password minimo 8 caratteri';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Le password non coincidono';
      }

      if (!formData.nome.trim()) {
        newErrors.nome = 'Nome obbligatorio';
      }

      if (!formData.cognome.trim()) {
        newErrors.cognome = 'Cognome obbligatorio';
      }

      if (!formData.telefono) {
        newErrors.telefono = 'Telefono obbligatorio';
      }
    }

    if (step === 2) {
      if (!formData.property_name.trim()) {
        newErrors.property_name = 'Nome struttura obbligatorio';
      }

      if (!formData.city.trim()) {
        newErrors.city = 'Città obbligatoria';
      }

      if (!formData.address.trim()) {
        newErrors.address = 'Indirizzo obbligatorio';
      }
    }

    if (step === 4) {
      if (!formData.room_name.trim()) {
        newErrors.room_name = 'Nome camera obbligatorio';
      }

      if (formData.room_capacity < 1) {
        newErrors.room_capacity = 'Capacità minima 1 ospite';
      }

      if (formData.price_per_night < 1) {
        newErrors.price_per_night = 'Prezzo minimo €1';
      }

      if (!formData.acceptTerms) {
        newErrors.acceptTerms = 'Devi accettare i Termini e Condizioni';
      }

      if (!formData.acceptPrivacy) {
        newErrors.acceptPrivacy = 'Devi accettare la Privacy Policy';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const toggleAmenity = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(id => id !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      return;
    }

    setLoading(true);

    try {
      // Step 1: Register user
      const userResponse = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        full_name: `${formData.nome} ${formData.cognome}`
      });

      const token = userResponse.data.token;

      // Step 2: Create property
      const propertyResponse = await axios.post(
        `${BACKEND_URL}/api/properties`,
        {
          name: formData.property_name,
          property_type: formData.property_type,
          city: formData.city,
          address: formData.address,
          description: formData.description,
          whatsapp: formData.telefono
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const propertyId = propertyResponse.data.id;

      // Step 3: Create room type
      await axios.post(
        `${BACKEND_URL}/api/properties/${propertyId}/room-types`,
        {
          name: formData.room_name,
          capacity: parseInt(formData.room_capacity),
          default_external_price: parseFloat(formData.price_per_night) * 1.2, // +20% as external reference
          default_traveldiscount_price: parseFloat(formData.price_per_night),
          description: 'Camera standard'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('🎉 Struttura registrata con successo!');
      
      // Save token and redirect to dashboard
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userResponse.data.user));
      
      setTimeout(() => {
        navigate('/owner/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div className={`
            flex items-center justify-center w-10 h-10 rounded-full font-bold
            ${currentStep >= step 
              ? 'bg-blue-600 text-white' 
              : 'bg-slate-200 text-slate-400'}
            transition-all duration-300
          `}>
            {step}
          </div>
          {step < 4 && (
            <div className={`
              w-16 h-1 mx-2
              ${currentStep > step ? 'bg-blue-600' : 'bg-slate-200'}
              transition-all duration-300
            `} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <Card className="max-w-3xl mx-auto backdrop-blur-sm bg-white/90 border border-slate-200 shadow-2xl">
      <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white p-6 rounded-t-xl">
        <h2 className="text-2xl font-bold">Registra la tua Struttura</h2>
        <p className="text-orange-100 text-sm mt-1">
          Step {currentStep} di 4
        </p>
      </div>

      <div className="p-8">
        {renderStepIndicator()}

        {/* STEP 1: Account Info */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Crea il tuo Account
              </h3>
              <p className="text-sm text-slate-600">
                Iniziamo con le tue informazioni personali
              </p>
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-orange-600" />
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tuo@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password" className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-orange-600" />
                  Password *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimo 8 caratteri"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={errors.password ? 'border-red-500' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-orange-600" />
                  Conferma Password *
                </Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ripeti password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                />
                {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome" className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-orange-600" />
                  Nome *
                </Label>
                <Input
                  id="nome"
                  placeholder="Mario"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className={errors.nome ? 'border-red-500' : ''}
                />
                {errors.nome && <p className="text-xs text-red-600 mt-1">{errors.nome}</p>}
              </div>

              <div>
                <Label htmlFor="cognome" className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-orange-600" />
                  Cognome *
                </Label>
                <Input
                  id="cognome"
                  placeholder="Rossi"
                  value={formData.cognome}
                  onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                  className={errors.cognome ? 'border-red-500' : ''}
                />
                {errors.cognome && <p className="text-xs text-red-600 mt-1">{errors.cognome}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="telefono" className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-orange-600" />
                Telefono / WhatsApp *
              </Label>
              <Input
                id="telefono"
                type="tel"
                placeholder="+39 333 1234567"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className={errors.telefono ? 'border-red-500' : ''}
              />
              {errors.telefono && <p className="text-xs text-red-600 mt-1">{errors.telefono}</p>}
            </div>

            <Button
              onClick={handleNext}
              className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 h-12"
            >
              Continua
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* STEP 2: Property Info */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Informazioni sulla Struttura
              </h3>
              <p className="text-sm text-slate-600">
                Dicci di più sulla tua proprietà
              </p>
            </div>

            <div>
              <Label htmlFor="property_name" className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-orange-600" />
                Nome Struttura *
              </Label>
              <Input
                id="property_name"
                placeholder="Hotel Bella Vista"
                value={formData.property_name}
                onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
                className={errors.property_name ? 'border-red-500' : ''}
              />
              {errors.property_name && <p className="text-xs text-red-600 mt-1">{errors.property_name}</p>}
            </div>

            <div>
              <Label htmlFor="property_type" className="flex items-center gap-2 mb-2">
                <Home className="w-4 h-4 text-orange-600" />
                Tipo di Struttura *
              </Label>
              <select
                id="property_type"
                value={formData.property_type}
                onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              >
                {propertyTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-orange-600" />
                  Città *
                </Label>
                <Input
                  id="city"
                  placeholder="Milano"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className={errors.city ? 'border-red-500' : ''}
                />
                {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
              </div>

              <div>
                <Label htmlFor="address" className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-orange-600" />
                  Indirizzo *
                </Label>
                <Input
                  id="address"
                  placeholder="Via Roma 123"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className={errors.address ? 'border-red-500' : ''}
                />
                {errors.address && <p className="text-xs text-red-600 mt-1">{errors.address}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="description" className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-orange-600" />
                Descrizione (opzionale)
              </Label>
              <Textarea
                id="description"
                placeholder="Descrivi la tua struttura, i servizi offerti, la posizione..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 h-12"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Indietro
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 h-12"
              >
                Continua
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Amenities & Policies */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Servizi e Orari
              </h3>
              <p className="text-sm text-slate-600">
                Seleziona i servizi offerti e gli orari
              </p>
            </div>

            <div>
              <Label className="mb-3 block font-semibold">Servizi Disponibili</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {availableAmenities.map(amenity => (
                  <div
                    key={amenity.id}
                    onClick={() => toggleAmenity(amenity.id)}
                    className={`
                      p-3 border-2 rounded-lg cursor-pointer transition-all
                      ${formData.amenities.includes(amenity.id)
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200 hover:border-slate-300'}
                    `}
                  >
                    <div className="text-2xl mb-1">{amenity.icon}</div>
                    <div className="text-xs font-medium text-slate-700">{amenity.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-3 block font-semibold">Orari Check-in / Check-out</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Check-in Da</Label>
                  <Input
                    type="time"
                    value={formData.checkin_from}
                    onChange={(e) => setFormData({ ...formData, checkin_from: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Check-in A</Label>
                  <Input
                    type="time"
                    value={formData.checkin_to}
                    onChange={(e) => setFormData({ ...formData, checkin_to: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Check-out Da</Label>
                  <Input
                    type="time"
                    value={formData.checkout_from}
                    onChange={(e) => setFormData({ ...formData, checkout_from: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Check-out A</Label>
                  <Input
                    type="time"
                    value={formData.checkout_to}
                    onChange={(e) => setFormData({ ...formData, checkout_to: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 h-12"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Indietro
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 h-12"
              >
                Continua
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Room Info */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Prima Camera/Unità
              </h3>
              <p className="text-sm text-slate-600">
                Aggiungi le informazioni base sulla tua prima unità
              </p>
            </div>

            <div>
              <Label htmlFor="room_name" className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-orange-600" />
                Nome Camera/Unità *
              </Label>
              <Input
                id="room_name"
                placeholder="Camera Standard, Suite Deluxe, Appartamento..."
                value={formData.room_name}
                onChange={(e) => setFormData({ ...formData, room_name: e.target.value })}
                className={errors.room_name ? 'border-red-500' : ''}
              />
              {errors.room_name && <p className="text-xs text-red-600 mt-1">{errors.room_name}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="room_capacity" className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-orange-600" />
                  Capacità Ospiti *
                </Label>
                <Input
                  id="room_capacity"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.room_capacity}
                  onChange={(e) => setFormData({ ...formData, room_capacity: e.target.value })}
                  className={errors.room_capacity ? 'border-red-500' : ''}
                />
                {errors.room_capacity && <p className="text-xs text-red-600 mt-1">{errors.room_capacity}</p>}
              </div>

              <div>
                <Label htmlFor="price_per_night" className="flex items-center gap-2 mb-2">
                  <Euro className="w-4 h-4 text-orange-600" />
                  Prezzo per Notte (€) *
                </Label>
                <Input
                  id="price_per_night"
                  type="number"
                  min="1"
                  step="0.01"
                  value={formData.price_per_night}
                  onChange={(e) => setFormData({ ...formData, price_per_night: e.target.value })}
                  className={errors.price_per_night ? 'border-red-500' : ''}
                />
                {errors.price_per_night && <p className="text-xs text-red-600 mt-1">{errors.price_per_night}</p>}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                ℹ️ <strong>Nota:</strong> Potrai aggiungere altre camere/unità e gestire disponibilità e prezzi dalla tua dashboard dopo la registrazione.
              </p>
            </div>

            {/* Legal Checkboxes */}
            <div className="space-y-4 border-t border-slate-200 pt-6">
              <h4 className="font-semibold text-slate-900">Consensi Obbligatori</h4>
              
              <div className="flex items-start gap-3">
                <Checkbox
                  id="acceptTerms"
                  checked={formData.acceptTerms}
                  onCheckedChange={(checked) => setFormData({ ...formData, acceptTerms: checked })}
                  className={errors.acceptTerms ? 'border-red-500' : ''}
                />
                <div className="flex-1">
                  <Label htmlFor="acceptTerms" className="text-sm text-slate-700 cursor-pointer">
                    Accetto i{' '}
                    <a 
                      href="/terms" 
                      target="_blank" 
                      className="text-orange-600 hover:text-orange-700 underline font-semibold"
                    >
                      Termini e Condizioni
                    </a>
                    {' '}del servizio *
                  </Label>
                  {errors.acceptTerms && <p className="text-xs text-red-600 mt-1">{errors.acceptTerms}</p>}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="acceptPrivacy"
                  checked={formData.acceptPrivacy}
                  onCheckedChange={(checked) => setFormData({ ...formData, acceptPrivacy: checked })}
                  className={errors.acceptPrivacy ? 'border-red-500' : ''}
                />
                <div className="flex-1">
                  <Label htmlFor="acceptPrivacy" className="text-sm text-slate-700 cursor-pointer">
                    Ho letto e accetto la{' '}
                    <a 
                      href="/privacy" 
                      target="_blank" 
                      className="text-orange-600 hover:text-orange-700 underline font-semibold"
                    >
                      Privacy Policy
                    </a>
                    {' '}e il trattamento dei miei dati *
                  </Label>
                  {errors.acceptPrivacy && <p className="text-xs text-red-600 mt-1">{errors.acceptPrivacy}</p>}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 h-12"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Indietro
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 h-12 shadow-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Registrazione...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Completa Registrazione
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default PropertyRegistrationForm;
